/**
 * Overflow menu for a single property row. Lazy-loads the property's row options when the row
 * renders, builds the menu entirely from that data, and disables the menu trigger while the
 * request is in flight.
 */
import { AccountViewListingOptionType } from '@zg-rentals/ts-rental-manager-api-client';

import { usePropertyTablePropertyOptions } from '../../../hooks/queries/propertyTable';

import { buildOverflowMenuItems, OverflowMenu } from './OverflowMenu';

import type { ListingAlias, RentalManagerAddressDto } from '@zg-rentals/ts-rental-manager-api-client';

type Props = {
  groupId: string;
  listingAlias: ListingAlias;
  addressLabel: string;
  address: RentalManagerAddressDto;
  onArchiveClick: (listingAlias: ListingAlias, address: RentalManagerAddressDto) => void;
  onEditClick: () => void;
};

const PropertyOverflowMenu = ({ groupId, listingAlias, addressLabel, address, onArchiveClick, onEditClick }: Props) => {
  const { data, isFetching } = usePropertyTablePropertyOptions(groupId);

  const items = buildOverflowMenuItems(data?.options ?? [], {
    actionOverrides: {
      // TODO: ARCHIVE may return an API ctaUrl + ctaRequest (e.g. POST
      // /api/web/v1/users/properties/multiplex/archive with { groupId }).
      // Call the API and keep/adapt the archive modal flow instead of only opening the modal.
      [AccountViewListingOptionType.ARCHIVE]: () => onArchiveClick(listingAlias, address),
      [AccountViewListingOptionType.EDIT_LISTING]: () => onEditClick(),
    },
  });

  return (
    <OverflowMenu
      triggerLabel={addressLabel ? `Options for ${addressLabel}` : 'Property options'}
      items={items}
      disabled={isFetching}
    />
  );
};

export default PropertyOverflowMenu;
